/**
 * ProviderServicesAnswerSheet - "Serviços executados" no padrão premium do app do prestador.
 *
 * Folha de baixo (mesma do "Concluir ordem"), cartões do app, Sim/Não grandes,
 * motivo obrigatório no Não (BR-OS-047) e observação recolhida.
 * Reaproveita as regras puras de serviceItems; o desktop segue com o diálogo próprio.
 *
 * RELEASE: 2026-10-17 v1.0.0 - Versão inicial.
 */
import { useEffect, useState } from 'react';
import { Check, Loader2, Plus, X } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { PROVIDER_DASHBOARD_CARD } from '@/components/dashboard/shared/dashboardCardStyles';
import {
  initialServiceAnswers, toServiceResultsPayload, validateServiceAnswers,
  type ServiceAnswer, type ServiceOrderServiceItem,
} from '@/lib/serviceOrders/serviceItems';

interface Props {
  open: boolean;
  onClose: () => void;
  items: ServiceOrderServiceItem[];
  saving?: boolean;
  onConfirm: (payload: ReturnType<typeof toServiceResultsPayload>) => void;
}

/** Erro do cartão (mesmas regras de validateServiceAnswers, por item). */
function itemError(a: ServiceAnswer): string | null {
  if (a.done === null) return 'Escolha Sim ou Não';
  if (a.done === false && !a.reason.trim()) return 'Informe o motivo';
  return null;
}

export function ProviderServicesAnswerSheet({ open, onClose, items, saving, onConfirm }: Props) {
  const [answers, setAnswers] = useState<ServiceAnswer[]>([]);
  const [showNotes, setShowNotes] = useState<Record<string, boolean>>({});
  const [tried, setTried] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const init = initialServiceAnswers(items);
    setAnswers(init);
    setShowNotes(Object.fromEntries(init.map((a) => [a.id, !!a.notes])));
    setTried(false); setError(null);
  }, [open, items]);

  const patch = (id: string, p: Partial<ServiceAnswer>) => setAnswers((l) => l.map((a) => (a.id === id ? { ...a, ...p } : a)));
  const answered = answers.filter((a) => a.done !== null).length;

  const submit = () => {
    setTried(true);
    const err = validateServiceAnswers(answers);
    setError(err);
    if (!err) onConfirm(toServiceResultsPayload(answers));
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="bottom" enableSwipeToClose={false} aria-describedby={undefined}
        className="provider-app mx-auto flex h-[100dvh] w-full max-w-2xl flex-col overflow-hidden bg-background p-0 text-foreground sm:h-[85dvh] sm:rounded-t-lg">
        <SheetHeader className="shrink-0 space-y-1 border-b px-4 pb-3 pt-4 pr-12 text-left">
          <SheetTitle className="text-lg font-bold">Serviços executados</SheetTitle>
          <p className="text-xs text-muted-foreground">{answered} de {answers.length} respondidos · No "Não", o motivo é obrigatório.</p>
        </SheetHeader>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
          {items.map((item) => {
            const a = answers.find((x) => x.id === item.id);
            if (!a) return null;
            const err = tried ? itemError(a) : null;
            return (
              <div key={item.id} className={cn(PROVIDER_DASHBOARD_CARD, 'space-y-3 border p-4', err && 'border-destructive')}>
                <div>
                  <p className="text-sm font-semibold">{item.product_name}</p>
                  <p className="text-xs text-muted-foreground">Quantidade: {Number(item.quantity)}</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button type="button" variant="outline" onClick={() => patch(item.id, { done: true })}
                    className={cn('h-11', a.done === true && 'border-success bg-success text-success-foreground hover:bg-success/90 hover:text-success-foreground')}>
                    <Check className="mr-1.5 h-4 w-4" />Sim
                  </Button>
                  <Button type="button" variant="outline" onClick={() => patch(item.id, { done: false })}
                    className={cn('h-11', a.done === false && 'border-destructive bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:text-destructive-foreground')}>
                    <X className="mr-1.5 h-4 w-4" />Não
                  </Button>
                </div>
                {a.done === false && (
                  <Textarea rows={2} maxLength={500} value={a.reason} placeholder="Motivo de não ter realizado *"
                    onChange={(e) => patch(item.id, { reason: e.target.value })} />
                )}
                {showNotes[item.id] ? (
                  <Textarea rows={2} maxLength={500} value={a.notes} placeholder="Observação (opcional)"
                    onChange={(e) => patch(item.id, { notes: e.target.value })} />
                ) : (
                  <button type="button" className="flex items-center gap-1 text-xs font-medium text-primary"
                    onClick={() => setShowNotes((s) => ({ ...s, [item.id]: true }))}>
                    <Plus className="h-3.5 w-3.5" />Adicionar observação
                  </button>
                )}
                {err && <p className="text-xs font-medium text-destructive">{err}</p>}
              </div>
            );
          })}
        </div>
        <div className="shrink-0 space-y-2 border-t bg-card px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
          {error && <p className="text-center text-xs font-medium text-destructive">{error}</p>}
          <Button className="h-12 w-full" disabled={saving} onClick={submit}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salvar e continuar
          </Button>
          <Button variant="ghost" className="h-10 w-full" onClick={onClose}>Voltar</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
